/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react'
import {
  GoogleMap,
  Marker,
  GoogleMarkerClusterer,
} from '@react-google-maps/api'
import { cx } from 'class-variance-authority'
import useGoogleMapsLoader from '../../../ui/useGoogleMapsLoader'
import { OfferAlgoliaSearchResult } from '../../../search'
import { ActiveMapOffer } from './map/ActiveMapOffer'
import { defaultMapCenter } from '../../../../Utils/map'
import { PaginatedList } from './PaginatedList'
import { useSmoothScroll } from '../../../../Utils/ui'

interface Location {
  latitude: number
  longitude: number
  postalCode: string
  street: string
  city: string
  name?: string
}

interface LocationMakerProps {
  location: Location
  clusterer?: unknown
  handleClick: () => void
}

export const OfferListMap = ({
  googleApiKey,
  list = [],
  totalItems = 0,
}: {
  googleApiKey: string
  list?: OfferAlgoliaSearchResult[]
  totalItems?: number
}) => {
  const [center, setCenter] = useState(defaultMapCenter)
  const [zoom, setZoom] = useState(5.3)
  const [active, setActive] = useState<OfferAlgoliaSearchResult | null>(null)

  const isLoaded = useGoogleMapsLoader(googleApiKey)

  if (!isLoaded) {
    return null
  }

  return (
    <section className="relative grid-flow-row-dense py-4 mx-auto lg:grid-cols-3 lg:grid 2xl:grid-cols-4">
      <div className="h-full min-h-[600px] lg:col-span-2 2xl:col-span-3 lg:order-last">
        <GoogleMap
          mapContainerClassName="lg:rounded-xl min-h-[750px] lg:min-h-[680px]"
          center={center}
          zoom={zoom}
          options={{
            disableDefaultUI: true,
            gestureHandling: 'greedy', // DOC https://tomchentw.github.io/react-google-maps/#googlemap
            streetViewControl: false,
          }}
        >
          <GoogleMarkerClusterer>
            {(clusterer) =>
              list?.map((hit) => {
                if (!hit) {
                  return null
                }
                const { _geoloc, slug } = hit

                return (
                  <LocationMarker
                    key={slug}
                    clusterer={clusterer}
                    location={{
                      latitude: _geoloc?.lat,
                      longitude: _geoloc?.lng,
                      postalCode: hit.office.postalCode,
                      city: hit.office.city,
                      street: '',
                      name: hit.office.name || hit.firm.name,
                    }}
                    handleClick={() => {
                      setZoom(14)
                      setCenter({
                        lat: hit?._geoloc?.lat,
                        lng: hit?._geoloc?.lng,
                      })
                      const scrollTo = useSmoothScroll(hit.slug)
                      setActive(hit)
                      scrollTo()
                    }}
                  />
                )
              })
            }
          </GoogleMarkerClusterer>
        </GoogleMap>
      </div>

      <section className="hidden w-full col-span-1 lg:block lg:static max-h-[680px] overflow-y-scroll ml-auto flex-1">
        <PaginatedList
          items={list}
          activeItem={active || undefined}
          totalItems={totalItems}
          renderItem={(hit, index) => (
            <div
              key={hit.slug}
              className={cx(
                'transition-transform transform-gpu duration-100 md:duration-500 ease-out opacity-0',
                'translate-y-4 animate-slide-in',
                'scale-95 animate-zoom-in',
                'animate-fade-in',
              )}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <ActiveMapOffer
                offer={hit}
                setUnactive={() => undefined}
                isInList
              />
            </div>
          )}
        />
      </section>
      {active && (
        <ActiveMapOffer offer={active} setUnactive={() => setActive(null)} />
      )}
    </section>
  )
}

const LocationMarker = ({
  location,
  clusterer,
  handleClick,
}: LocationMakerProps) => {
  if (!location?.latitude || !location?.longitude) {
    return null
  }

  return (
    <Marker
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      clusterer={clusterer as any}
      position={{ lat: location.latitude, lng: location.longitude }}
      title={`${location.name || ''} ${location.street} ${location.postalCode
        } ${location.city}`}
      onClick={handleClick}
    />
  )
}
